import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, rootApi, type BusinessOverview, type BusinessSummary } from "./api";
import { navFor, type NavItem } from "./nav";

interface BusinessValue {
  data: BusinessOverview;
  navItems: NavItem[];
  reload: () => void;
  /** Whether a solution is switched on here. */
  has: (solution: string) => boolean;
}

const BusinessContext = createContext<BusinessValue | null>(null);

export function BusinessProvider({ data, reload, children }: { data: BusinessOverview; reload: () => void; children: ReactNode }) {
  // One value per overview, so pages and the shell don't redo their work on every render.
  const value = useMemo<BusinessValue>(() => {
    const on = data.solutions ?? [];
    return { data, navItems: navFor(on), reload, has: (s) => on.includes(s) };
  }, [data, reload]);
  return <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>;
}

export function useBusiness(): BusinessValue {
  const v = useContext(BusinessContext);
  if (!v) throw new Error("useBusiness outside a business");
  return v;
}

export function useOverviewQuery(handle: string) {
  return useQuery({ queryKey: ["business", handle], queryFn: () => api<BusinessOverview>(""), retry: false, staleTime: 60_000 });
}

/** Every business the signed-in person belongs to (the business menu). */
export function useBusinesses() {
  return useQuery({ queryKey: ["businesses"], queryFn: () => rootApi<{ businesses: BusinessSummary[] }>("/businesses").then((r) => r.businesses), staleTime: 60_000 });
}

const LAST = "mivio:last-business";
export function rememberBusiness(handle: string): void {
  try {
    localStorage.setItem(LAST, handle);
  } catch {
    // private windows keep nothing
  }
}
export function lastBusiness(): string | null {
  try {
    return localStorage.getItem(LAST);
  } catch {
    return null;
  }
}
